import { computed, inject } from '@angular/core';
import { type Observable, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { type WorkupPersonAttachment } from '../../types';
import { AttachmentsService } from './attachments.service';
import { AuthStore } from '../auth';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { setError, setLoaded, setLoading, withCallState, withReset } from '@ngrx-toolkit/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

type State = {
  attachments: Array<WorkupPersonAttachment>;
  parkingAttachments: Array<WorkupPersonAttachment>;
};

const initialState: State = {
  attachments: [],
  parkingAttachments: [],
};

export const AttachmentsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCallState(),
  withReset(),
  withComputed(({ attachments }) => ({
    signature: computed(() => attachments().find((attachment) => attachment.type === 'SIGNATURE')),
    photo: computed(() => attachments().find((attachment) => attachment.type === 'PHOTO')),
    parkingPhoto: computed(() => attachments().find((attachment) => attachment.type === 'PARKING_PHOTO')),
  })),
  withMethods((store, api = inject(AttachmentsService)) => ({
    load: rxMethod<void>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap(() =>
          api.loadAttachments().pipe(
            tapResponse({
              next: (attachments) => patchState(store, { attachments }, setLoaded()),
              error: (error) => patchState(store, setError(error)),
            }),
          ),
        ),
      ),
    ),
    download: (type: string): Observable<Blob> => {
      patchState(store, setLoading());
      return api.downloadAttachmentByType(type).pipe(
        tap({
          next: () => patchState(store, setLoaded()),
          error: (error) => patchState(store, setError(error)),
        }),
      );
    },
    loadParking: rxMethod<void>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap(() =>
          api.loadParkingAttachments().pipe(
            tapResponse({
              next: (parkingAttachments) => patchState(store, { parkingAttachments }, setLoaded()),
              error: (error) => patchState(store, setError(error)),
            }),
          ),
        ),
      ),
    ),
    downloadParking: (id: string): Observable<Blob> => {
      patchState(store, setLoading());
      return api.downloadParkingAttachment(id).pipe(
        tap({
          next: () => patchState(store, setLoaded()),
          error: (error) => patchState(store, setError(error)),
        }),
      );
    },
  })),
  withHooks({
    onInit(store, authStore = inject(AuthStore)) {
      authStore.logout$.subscribe(() => store.resetState());
    },
  }),
);
